/**
 * LookupCombobox — combobox autocomplete per i campi ISTAT (comuni / stati).
 *
 * Props:
 *   endpoint   : '/public/checkin/lookup/comuni' | '/public/checkin/lookup/stati'
 *   value      : codice attuale (es. "H501" o "100000100")
 *   onChange   : (item) → ({code, name, province?})
 *   placeholder, label, disabled
 *
 * Behavior:
 *   - Click → mostra dropdown con i primi 30 risultati
 *   - Type → debounced search (250ms) sulla API
 *   - Su blur, se valore non corrisponde a un'opzione esistente lo conserva
 *     come stringa libera (utile per fallback manuale).
 *   - Mostra "code · name" nelle option, "name" nel main input.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function LookupCombobox({
  endpoint,
  value,
  onChange,
  placeholder = 'Cerca...',
  disabled = false,
  required = false,
  testid,
  initialName = '',  // optional pre-resolved name
  province,           // optional comuni filter
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedName, setResolvedName] = useState(initialName || '');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve initial name from code (only once on mount or when value changes externally)
  useEffect(() => {
    let alive = true;
    if (!value) {
      setResolvedName('');
      return;
    }
    if (resolvedName && resolvedName !== '__pending__') return;
    const fetchName = async () => {
      try {
        const res = await axios.get(`${API}${endpoint}?q=${encodeURIComponent(value)}&limit=5`);
        if (!alive) return;
        const match = (res.data?.items || []).find(it => it.code === value);
        if (match) setResolvedName(match.name);
      } catch (_) {
        // Silent fail — value will display as code only
      }
    };
    fetchName();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, endpoint]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query, limit: '30' });
        if (province) params.set('province', province);
        const res = await axios.get(`${API}${endpoint}?${params}`);
        if (!alive) return;
        setItems(res.data?.items || []);
      } catch (_) {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [query, open, endpoint, province]);

  // Click outside → close
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const select = (item) => {
    setResolvedName(item.name);
    setQuery('');
    setOpen(false);
    onChange(item);
  };

  const displayValue = open ? query : (resolvedName || value || '');

  return (
    <div ref={wrapperRef} className="relative" data-testid={testid}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          placeholder={placeholder}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          required={required}
          className="form-input pr-9 uppercase"
          autoComplete="off"
          data-testid={testid ? `${testid}-input` : undefined}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => { setOpen(o => !o); if (!open) inputRef.current?.focus(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
          data-testid={testid ? `${testid}-toggle` : undefined}
        >
          <CaretDown size={14} weight="bold" />
        </button>
      </div>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-zinc-300 shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <div className="p-3 text-[11px] text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
              <MagnifyingGlass size={11} weight="bold" /> Ricerca…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-3 text-[11px] text-zinc-400 uppercase tracking-wider">Nessun risultato</div>
          )}
          {!loading && items.map((it) => (
            <button
              type="button"
              key={it.code}
              onClick={() => select(it)}
              className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-zinc-100 last:border-b-0 transition-colors"
              data-testid={testid ? `${testid}-option-${it.code}` : undefined}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-bold text-zinc-900 truncate">{it.name}</span>
                <span className="text-[10px] font-mono text-zinc-400 flex-shrink-0">
                  {it.code}{it.province ? ` · ${it.province}` : ''}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Hidden styled like form-input */}
      <style>{`.form-input{width:100%;height:40px;padding:0 12px;border:1px solid #d4d4d8;font-size:14px}.form-input:focus{outline:none;border-color:#7c3aed}`}</style>
    </div>
  );
}
